import { FC, useEffect, useMemo, useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import LinearProgress from '@material-ui/core/LinearProgress';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { resolveQueueErrorMessage } from '../Initialize/resolveQueueErrorMessage';

import exerciseQueuesApi, { exerciseQueuesApiKeys } from 'api/rest/exerciseQueues/exerciseQueues';
import type {
  ExerciseAttempt,
  ExerciseQueue,
  ExerciseQueueExplainRequestDto,
  ExerciseQueueUuid,
} from 'api/rest/exerciseQueues/exerciseQueues.types';
import exercisesApi from 'api/rest/exercises/exercises';
import { ExerciseFileTypes } from 'api/rest/exercises/exercises.types';
import type { ExerciseAnswer } from 'api/rest/exercises/exercises.types';
import { ReactComponent as TablesIcon } from 'assets/icons/general/tables_white.svg';
import ImageWithLoaderWrapper from 'components/ImageWithLoaderWrapper/ImageWithLoaderWrapper';
import LatexText from 'components/LatexText/LatexText';
import SubjectIcon from 'components/SubjectIcon/SubjectIcon';
import QUERY from 'constants/queryKeys';
import { useTablesContext } from 'context/TablesContext';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import type { Subject } from 'types/Subject';

import OnGoLayout from '../OnGoLayout';
import { ON_GO_COLORS } from '../onGoColors';
import { getSelectedAnswers, getChoicesSectionTitle, isAnswerSelectionComplete, resolveExerciseSelectionMode, toggleMultiSelectAnswer } from './groupExerciseAnswers';
import OnGoExerciseChoices from './OnGoExerciseChoices';
import { resolveOnGoChoiceDisplayState } from './onGoChoiceDisplay';
import { formatAnswerLabels, resolveCorrectAnswers } from './resolveCorrectAnswers';
import useWritingAnimation from './useWritingAnimation';

const TABLES_SUBJECTS = ['CH', 'B', 'FI', 'M', 'MR', 'GE'];

/** Per-question flow: pick/skip, then review feedback before advancing. */
export type ExercisePhase = 'question' | 'result';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
  },
  titleGroup: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
  },
  title: {
    fontWeight: 600,
    lineHeight: 1.2,
  },
  progressTrack: {
    width: '100%',
    height: 8,
    borderRadius: 999,
    overflow: 'hidden',
    marginBottom: theme.spacing(2.5),
  },
  progressBarAnimated: {
    transition: 'transform 420ms ease',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    flexShrink: 0,
  },
  abortButton: {
    textTransform: 'none',
    color: theme.palette.text.secondary,
  },
  tablesButton: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fill: theme.palette.common.white,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
    },
    '& svg': {
      width: 20,
      height: 20,
    },
    '&$disabled': {
      backgroundColor: theme.palette.grey[300],
      borderColor: theme.palette.grey[300],
      fill: theme.palette.common.white,
      color: theme.palette.common.white,
    },
  },
  disabled: {},
  loader: {
    minHeight: 280,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageSection: {
    marginBottom: theme.spacing(2.5),
  },
  choicesSection: {
    display: 'grid',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  choice: {
    width: '100%',
    justifyContent: 'flex-start',
    textAlign: 'left',
    padding: theme.spacing(1.5, 2),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color 120ms ease, background-color 120ms ease',
  },
  selectedChoice: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  correctChoice: {
    border: `2px solid ${ON_GO_COLORS.correct}`,
    backgroundColor: ON_GO_COLORS.correctSoft,
  },
  incorrectChoice: {
    border: `2px solid ${ON_GO_COLORS.incorrect}`,
    backgroundColor: ON_GO_COLORS.incorrectSoft,
  },
  mutedChoice: {
    opacity: 0.7,
  },
  result: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  correctResult: {
    border: `2px solid ${ON_GO_COLORS.correct}`,
    backgroundColor: ON_GO_COLORS.correctSoft,
    color: ON_GO_COLORS.correct,
  },
  incorrectResult: {
    border: `2px solid ${ON_GO_COLORS.incorrect}`,
    backgroundColor: ON_GO_COLORS.incorrectSoft,
    color: ON_GO_COLORS.incorrect,
  },
  resultHeading: {
    fontWeight: 700,
  },
  resultText: {
    color: theme.palette.text.primary,
  },
  explanationButton: {
    alignSelf: 'flex-start',
    textTransform: 'none',
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
  },
  explanationButtonIncorrect: {
    color: ON_GO_COLORS.incorrect,
    borderColor: ON_GO_COLORS.incorrect,
    '&:hover': {
      borderColor: ON_GO_COLORS.incorrect,
      backgroundColor: ON_GO_COLORS.incorrectSoft,
    },
  },
  explanationText: {
    marginTop: theme.spacing(1),
    whiteSpace: 'pre-wrap',
    // KaTeX display math can be wider than the result card on small screens.
    '& .katex-display': {
      margin: theme.spacing(1, 0),
      overflowX: 'auto',
      overflowY: 'hidden',
    },
  },
  explanationCaret: {
    display: 'inline-block',
    width: 2,
    height: '1em',
    marginLeft: 1,
    verticalAlign: 'text-bottom',
    backgroundColor: 'currentColor',
    animation: '$blinkCaret 900ms step-end infinite',
  },
  '@keyframes blinkCaret': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
  explanationLoader: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  footer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
  },
  footerResult: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1.5),
  },
  errorSnackbar: {
    backgroundColor: theme.palette.error.dark,
    color: theme.palette.error.contrastText,
  },
}));

type AnswerResult = {
  attempt: ExerciseAttempt;
  nextQueue: ExerciseQueue;
};

type OnGoExerciseProps = {
  queueId: ExerciseQueueUuid;
  subject: Subject;
};

/** Aligns with `TablesContext` provider value (JSX context has no TypeScript shape). */
type TablesContextApi = {
  openCustomImages: (images: string[]) => void;
  openTables: () => void;
};

const OnGoExercise: FC<OnGoExerciseProps> = ({ queueId, subject }) => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<number[]>([]);
  const [answerResult, setAnswerResult] = useState<AnswerResult | null>(null);
  const [isExplanationVisible, setIsExplanationVisible] = useState(false);
  const [explainParams, setExplainParams] = useState<ExerciseQueueExplainRequestDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const tablesCtx = useTablesContext() as TablesContextApi | null;
  const { logEvent } = useUserLogContext();

  const phase: ExercisePhase = answerResult ? 'result' : 'question';

  const { data: queue, isLoading: isQueueLoading } = useQuery(exerciseQueuesApiKeys.detail(queueId), exerciseQueuesApi.getByUuid(queueId), {
    enabled: !!queueId,
    // The backend advances the queue right after answering, while the UI is still on the result
    // screen. A focus refetch would pull the advanced queue and reset the view mid-review.
    refetchOnWindowFocus: false,
  });

  const currentExerciseId = queue?.currentItem?.exerciseId;

  const { data: exercise, isLoading: isExerciseLoading } = useQuery(
    [QUERY.GET_SINGLE_EXERCISE, currentExerciseId],
    exercisesApi.getSingle(currentExerciseId!),
    {
      enabled: currentExerciseId != null,
      refetchOnWindowFocus: false,
    },
  );

  const answerMutation = useMutation(exerciseQueuesApi.answer(queueId), {
    onSuccess: ({ attempt, queue: nextQueue }) => {
      setAnswerResult({ attempt, nextQueue });
      logEvent('on-go-answer', {
        subjectPk: subject.pk,
        subjectCode: subject.subjectCode,
        queueUuid: queueId,
        correct: attempt.correct,
        exerciseIndex: exercise?.index,
      });
    },
    onError: error => {
      setErrorMessage(resolveQueueErrorMessage(error));
    },
  });

  const skipMutation = useMutation(exerciseQueuesApi.skip(queueId), {
    onSuccess: nextQueue => {
      queryClient.setQueryData(exerciseQueuesApiKeys.detail(queueId), nextQueue);
      queryClient.setQueryData(exerciseQueuesApiKeys.active(subject.pk), nextQueue);
      logEvent('on-go-skip', {
        subjectPk: subject.pk,
        subjectCode: subject.subjectCode,
        queueUuid: queueId,
      });
    },
    onError: error => {
      setErrorMessage(resolveQueueErrorMessage(error));
    },
  });

  const closeMutation = useMutation(exerciseQueuesApi.close(queueId), {
    onSuccess: () => {
      queryClient.setQueryData(exerciseQueuesApiKeys.active(subject.pk), null);
      queryClient.invalidateQueries(exerciseQueuesApiKeys.active(subject.pk));
    },
    onError: error => {
      setErrorMessage(resolveQueueErrorMessage(error));
    },
  });

  const explainQuery = useQuery(
    explainParams ? exerciseQueuesApiKeys.explain(explainParams) : ['exerciseQueues', 'explain', 'idle'],
    () => {
      return exerciseQueuesApi.explain()(explainParams!);
    },
    {
      enabled: explainParams != null,
      refetchOnWindowFocus: false,
      onError: error => {
        setIsExplanationVisible(false);
        setExplainParams(null);
        setErrorMessage(resolveQueueErrorMessage(error));
      },
    },
  );

  const explanationText = explainQuery.data?.text ?? null;
  const isExplanationLoading = explainQuery.isLoading || explainQuery.isFetching;
  const {
    displayedText: animatedExplanationText,
    isAnimating: isExplanationAnimating,
    reset: resetExplanationAnimation,
  } = useWritingAnimation(explanationText, { enabled: isExplanationVisible });

  useEffect(() => {
    if (currentExerciseId == null) {
      return;
    }
    setSelectedAnswerIds([]);
    setAnswerResult(null);
    setIsExplanationVisible(false);
    setExplainParams(null);
    resetExplanationAnimation();
  }, [currentExerciseId, resetExplanationAnimation]);

  const selectionMode = resolveExerciseSelectionMode(exercise?.form);

  const selectedAnswers = useMemo(
    () => (exercise?.answers ? getSelectedAnswers(exercise.answers, selectedAnswerIds) : []),
    [exercise?.answers, selectedAnswerIds],
  );

  const correctAnswers = useMemo(() => {
    if (!exercise?.answers || !answerResult) {
      return [];
    }
    return resolveCorrectAnswers(exercise.answers, answerResult.attempt, selectedAnswers);
  }, [exercise?.answers, answerResult, selectedAnswers]);

  const canSubmitAnswer = useMemo(() => {
    if (!exercise?.answers.length) {
      return false;
    }
    return isAnswerSelectionComplete(selectionMode, exercise.answers, selectedAnswerIds);
  }, [exercise?.answers, selectionMode, selectedAnswerIds]);

  const correctAnswerIdSet = useMemo(() => new Set(correctAnswers.map(answer => answer.id)), [correctAnswers]);

  const isSubmitting = answerMutation.isLoading || skipMutation.isLoading || closeMutation.isLoading;
  const isContentLoading = isQueueLoading || isExerciseLoading;
  const questionImages = exercise?.files[ExerciseFileTypes.question] || [];
  const attachmentImages = exercise?.files[ExerciseFileTypes.attachment] || [];
  const canOpenTables = subject.subjectCode === 'GE' ? attachmentImages.length > 0 : TABLES_SUBJECTS.includes(subject.subjectCode);
  const progressQueue = answerResult?.nextQueue ?? queue;
  const progressPercent = progressQueue?.progress.total
    ? Math.min(100, Math.max(0, (progressQueue.progress.answered / progressQueue.progress.total) * 100))
    : 0;
  // Title tracks answered progress (+1 while answering); skip advances the item but not `answered`.
  const exercisePosition =
    progressQueue?.progress.total != null
      ? phase === 'result'
        ? progressQueue.progress.answered
        : progressQueue.progress.answered + 1
      : null;
  const exerciseTitle =
    exercisePosition != null && progressQueue?.progress.total ? `Zadanie ${exercisePosition} z ${progressQueue.progress.total}` : 'Zadanie';

  const onCheckAnswer = () => {
    if (phase !== 'question' || !canSubmitAnswer) {
      return;
    }
    answerMutation.mutate({ answer_ids: selectedAnswerIds });
  };

  const onNextQuestion = () => {
    if (!answerResult) {
      return;
    }
    queryClient.setQueryData(exerciseQueuesApiKeys.active(subject.pk), answerResult.nextQueue);
    queryClient.setQueryData(exerciseQueuesApiKeys.detail(queueId), answerResult.nextQueue);
    setAnswerResult(null);
    setSelectedAnswerIds([]);
  };

  const onSkip = () => {
    if (phase === 'question' && !isSubmitting) {
      skipMutation.mutate();
    }
  };

  const onTablesClick = () => {
    if (subject.subjectCode === 'GE') {
      tablesCtx?.openCustomImages(attachmentImages);
      return;
    }
    tablesCtx?.openTables();
  };

  const onAbort = () => {
    if (isSubmitting) {
      return;
    }
    const confirmed = window.confirm('Postęp w tej kolejce zostanie utracony. Czy na pewno chcesz przerwać sesję?');
    if (!confirmed) {
      return;
    }
    logEvent('on-go-abort', {
      subjectPk: subject.pk,
      subjectCode: subject.subjectCode,
      queueUuid: queueId,
      progressAnswered: queue?.progress.answered,
    });
    closeMutation.mutate();
  };

  const selectedAnswerIdSet = useMemo(() => new Set(selectedAnswerIds), [selectedAnswerIds]);

  const onSelectSingleChoice = (answerId: number) => {
    setSelectedAnswerIds([answerId]);
  };

  const onSelectTrueFalseChoice = (answerId: number, group: number) => {
    setSelectedAnswerIds(previous => {
      const withoutGroup = previous.filter(id => {
        const answer = exercise?.answers.find(item => item.id === id);
        return answer?.group !== group;
      });
      return [...withoutGroup, answerId];
    });
  };

  const onToggleMultiSelectChoice = (answerId: number) => {
    setSelectedAnswerIds(previous => toggleMultiSelectAnswer(previous, answerId));
  };

  const getChoiceClassName = (answer: ExerciseAnswer) => {
    const displayState = resolveOnGoChoiceDisplayState(
      phase === 'result',
      selectedAnswerIdSet.has(answer.id),
      correctAnswerIdSet.has(answer.id),
      answerResult?.attempt.correct ?? null,
    );

    switch (displayState) {
      case 'question-selected':
        return clsx(classes.choice, classes.selectedChoice);
      case 'question-unselected':
        return classes.choice;
      case 'result-correct':
        return clsx(classes.choice, classes.correctChoice);
      case 'result-incorrect':
        return clsx(classes.choice, classes.incorrectChoice);
      case 'result-muted':
        return clsx(classes.choice, classes.mutedChoice);
    }
  };

  const correctAnswersLabel = correctAnswers.length > 0 ? formatAnswerLabels(correctAnswers) : null;
  const correctAnswersHeading = correctAnswers.length > 1 ? 'Poprawne odpowiedzi' : 'Poprawna odpowiedź';
  const nextButtonLabel = answerResult?.nextQueue.completedAt ? 'Podsumowanie' : 'Następne';
  const queueItemId = queue?.currentItem?.id;
  const canFetchExplanation = queueItemId != null;

  const onToggleExplanation = () => {
    if (!canFetchExplanation || queueItemId == null) {
      return;
    }

    if (isExplanationVisible) {
      setIsExplanationVisible(false);
      return;
    }

    setIsExplanationVisible(true);

    if (explanationText != null || isExplanationLoading) {
      return;
    }

    setExplainParams({
      item_id: queueItemId,
    });
  };

  return (
    <OnGoLayout
      footer={
        phase === 'question' ? (
          <Box className={classes.footer}>
            <Button disabled={isSubmitting} onClick={onSkip} variant='outlined'>
              Pomiń
            </Button>
            <Button color='primary' disabled={isSubmitting || !canSubmitAnswer} onClick={onCheckAnswer} variant='contained'>
              Dalej
            </Button>
          </Box>
        ) : (
          <Box className={classes.footerResult}>
            <Button color='primary' disabled={isSubmitting} fullWidth onClick={onNextQuestion} variant='contained'>
              {nextButtonLabel}
            </Button>
          </Box>
        )
      }
    >
      <Box className={classes.header}>
        <Box className={classes.titleGroup}>
          <SubjectIcon iconName={subject.subjectCode} size='s' />
          <Box minWidth={0}>
            <Typography className={classes.title} component='h1' noWrap variant='h6'>
              {exerciseTitle}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.headerActions}>
          <Button
            aria-label='Przerwij sesję'
            className={classes.abortButton}
            disabled={isSubmitting}
            onClick={onAbort}
            size='small'
            variant='text'
          >
            Przerwij
          </Button>
          <ButtonBase
            aria-label={subject.subjectCode === 'GE' ? 'Otwórz pomoce' : 'Otwórz tablice'}
            className={clsx(classes.tablesButton, !canOpenTables && classes.disabled)}
            disabled={!canOpenTables}
            onClick={onTablesClick}
          >
            <TablesIcon />
          </ButtonBase>
        </Box>
      </Box>
      {progressQueue ? (
        <LinearProgress
          aria-label='Postęp sesji'
          className={classes.progressTrack}
          classes={{ bar: classes.progressBarAnimated }}
          color='primary'
          value={progressPercent}
          variant='determinate'
        />
      ) : null}

      {isContentLoading ? (
        <Box className={classes.loader}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          <Box aria-label='Treść zadania' className={classes.imageSection} component='section'>
            {questionImages.length ? (
              <ImageWithLoaderWrapper
                loading={false}
                onLoad={() => undefined}
                setZoomed={setZoomed}
                urls={questionImages}
                zoomed={zoomed}
              />
            ) : (
              <Typography align='center' color='textSecondary'>
                Brak treści zadania do wyświetlenia.
              </Typography>
            )}
          </Box>

          {phase === 'result' && answerResult ? (
            <Box
              aria-live='polite'
              className={clsx(classes.result, answerResult.attempt.correct ? classes.correctResult : classes.incorrectResult)}
              role='status'
            >
              <Typography className={classes.resultHeading} variant='subtitle1'>
                {answerResult.attempt.correct ? 'Poprawna odpowiedź' : 'Niepoprawna odpowiedź'}
              </Typography>
              <Typography className={classes.resultText} variant='body2'>
                Twoja odpowiedź:{' '}
                {selectedAnswers.length > 0 ? formatAnswerLabels(selectedAnswers) : answerResult.attempt.answer}
              </Typography>
              {!answerResult.attempt.correct && correctAnswersLabel ? (
                <Typography className={classes.resultText} variant='body2'>
                  {correctAnswersHeading}: {correctAnswersLabel}
                </Typography>
              ) : null}
              {canFetchExplanation && !answerResult.attempt.correct ? (
                <Button
                  className={clsx(classes.explanationButton, classes.explanationButtonIncorrect)}
                  disabled={isExplanationLoading}
                  onClick={onToggleExplanation}
                  size='small'
                  variant='outlined'
                >
                  {isExplanationVisible ? 'Ukryj wyjaśnienie' : 'Pokaż wyjaśnienie'}
                </Button>
              ) : null}
              {isExplanationVisible ? (
                isExplanationLoading && explanationText == null ? (
                  <Box aria-busy='true' aria-label='Ładowanie wyjaśnienia' className={classes.explanationLoader}>
                    <CircularProgress size={18} />
                    <Typography className={classes.resultText} variant='body2'>
                      Ładowanie wyjaśnienia…
                    </Typography>
                  </Box>
                ) : explanationText ? (
                  <Typography
                    aria-busy={isExplanationAnimating}
                    aria-label='Wyjaśnienie'
                    className={clsx(classes.resultText, classes.explanationText)}
                    variant='body2'
                  >
                    <LatexText hideIncompleteMath={isExplanationAnimating} text={animatedExplanationText} />
                    {isExplanationAnimating ? <Box aria-hidden className={classes.explanationCaret} component='span' /> : null}
                  </Typography>
                ) : null
              ) : null}
            </Box>
          ) : null}

          <Box className={classes.choicesSection} component='section'>
            <Typography className={classes.sectionTitle} component='h2' variant='subtitle1'>
              {getChoicesSectionTitle(phase, selectionMode)}
            </Typography>
            {exercise?.answers.length ? (
              <OnGoExerciseChoices
                answers={exercise.answers}
                correctAnswerIdSet={correctAnswerIdSet}
                disabled={isSubmitting}
                getSingleChoiceClassName={getChoiceClassName}
                isAttemptCorrect={answerResult?.attempt.correct ?? null}
                isResultPhase={phase === 'result'}
                onSelectSingleChoice={onSelectSingleChoice}
                onSelectTrueFalseChoice={onSelectTrueFalseChoice}
                onToggleMultiSelectChoice={onToggleMultiSelectChoice}
                phase={phase}
                selectedAnswerIdSet={selectedAnswerIdSet}
                selectedAnswerIds={selectedAnswerIds}
                selectionMode={selectionMode}
              />
            ) : (
              <Typography color='textSecondary' variant='body2'>
                To zadanie nie ma jeszcze skonfigurowanych odpowiedzi.
              </Typography>
            )}
          </Box>
        </>
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        open={!!errorMessage}
      >
        <SnackbarContent className={classes.errorSnackbar} message={errorMessage ?? ''} role='alert' />
      </Snackbar>
    </OnGoLayout>
  );
};

export default OnGoExercise;
